import { Component, EventEmitter, Output, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { PoSelectOption, PoButtonModule, PoContainerModule, PoFieldModule, PoInputComponent, PoLoadingModule, PoWidgetModule, PoNotificationService, PoModalComponent, PoModalModule } from '@po-ui/ng-components';
import { ScannerComponent } from '../../../shared/components/scanner/scanner.component';
import { EmbarqueService } from '../../../shared/service/embarque.service';
import { TokenService } from '../../../shared/service/token.service';
import { AudioService } from '../../../shared/service/audio.service';
import { ToasterComponent } from '../../../shared/components/toaster/toaster.component';
import { ToasterService } from '../../../shared/service/toaster.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-departure-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PoButtonModule,
    PoContainerModule,
    PoFieldModule,
    PoLoadingModule,
    PoWidgetModule,
    PoModalModule,
    ScannerComponent,
    ReactiveFormsModule,
    ToasterComponent
  ],
  templateUrl: './departure-header.component.html',
  styleUrl: './departure-header.component.css'
})
export class DepartureHeaderComponent implements OnInit {
  constructor(
    private router: Router,
    private embarqueService: EmbarqueService,
    private tokenService: TokenService,
    private toasterService: ToasterService,
    private audioService: AudioService,
    private fb: FormBuilder
  ) {}
  @Output() isLoading = new EventEmitter<boolean>();
  @Output() onTerminalModalOpen = new EventEmitter();
  @ViewChild('invoiceInput') invoiceInput!: PoInputComponent;
  @ViewChild('modalSelectInvoice') modalSelectInvoice!: PoModalComponent;

  multipleInvoices: any[] = [];
  selectedInvoice: any = null;

  invoiceNumber: string = '';
  serialCode: number | undefined = undefined;
  filterForm!: FormGroup;
  statusFilter: string = 'all';
  statusFilterOptions: Array<PoSelectOption> = [
    { label: 'Todos', value: 'all' },
    { label: 'Pendente', value: 'pending' },
    { label: 'Em conferência', value: 'in-progress' },
    { label: 'Concluída', value: 'checked' }
  ];
  private readonly STORAGE_KEY = 'departureFilters';

  ngOnInit() {
    this.initFilterForm();
    this.loadInvoiceList();
  }

  private initFilterForm() {
    const stored = this.loadStoredFilters();
    const filterDate = stored?.filterDate ?? new Date();
    if (stored?.statusFilter) {
      this.statusFilter = stored.statusFilter;
    }

    this.filterForm = this.fb.group({
      filterDate: [filterDate]
    });
  }

  columns = [
    { property: 'notaFiscal', label: 'Nota fiscal' },
    { property: 'pedido', label: 'Nº Pedido' },
    { property: 'empresa', label: 'Empresa' },
    { property: 'fornecedor', label: 'Fornecedor' },
    { property: 'dataEmissao', label: 'Data emissão' },
    { property: 'status', label: 'Status', type: 'label', labels: [
      { value: 'pending', color: 'color-07', label: 'Pendente' },
      { value: 'checked', color: 'color-11', label: 'Concluída' }
    ]}
  ];

  invoiceList: any[] = [];
  displayInvoiceList: any[] = [];
  errorMessage: string | null = null;

  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 1;
  totalItems: number = 0;
  filteredTotalItems: number = 0;
  hasNextPage: boolean = false;
  isLoadingMore: boolean = false;

  onInvoiceClick(invoice: any) {
    this.invoiceNumber = String(invoice.notaFiscal ?? '');
    this.serialCode = invoice.serialCode ?? undefined;
    this.storeCurrentFilters();
    this.searchInvoice();
  }

  onScannedInvoice(barcode: string) {
    this.invoiceNumber = barcode;
    this.serialCode = undefined;
    this.storeCurrentFilters();
    this.searchInvoice();
  }

  searchInvoice() {
    if (!this.invoiceNumber) return;

    this.isLoading.emit(true);
    this.errorMessage = null;
    const branchCode = Number(this.tokenService.getBranch());

    const invoiceStr = String(this.invoiceNumber ?? '').trim();
    const invoiceMatch = invoiceStr.match(/\d+/);
    if (!invoiceMatch) {
      this.audioService.playBeep(5);
      this.toasterService.triggerAlert('Código de barras inválido', 'error');
      this.errorMessage = 'Código de barras inválido';
      this.isLoading.emit(false);
      return;
    }

    const invoiceCode = Number(invoiceMatch[0]);
    if (invoiceStr.length > 9) {
      this.embarqueService.getCheckingInvoicesAccessKey(
        invoiceMatch[0],
      ).pipe(
        finalize(() => {
          this.isLoading.emit(false);
        })
      ).subscribe({
        next: (response) => {
          if (response && response.items && response.items.length > 0) {
            if (response.items.length > 1) {
              this.multipleInvoices = response.items;
              this.openInvoiceSelectionModal();
            } else {
              const invoice = response.items[0];
              this.navigateToConference(invoice);
            }
          } else {
            this.audioService.playBeep(5);
            this.toasterService.triggerAlert('Nota fiscal não encontrada', 'error');
            this.errorMessage = 'Nota fiscal não encontrada';
          }
        },
        error: (error) => {
          this.audioService.playBeep(5);
          this.toasterService.triggerAlert('Erro ao buscar nota fiscal', 'error');
          this.errorMessage = 'Erro ao buscar nota fiscal';
        }
      });
    } else {
      this.embarqueService.getCheckingInvoices(
        branchCode,
        invoiceCode,
        this.serialCode,
        1,
        1000,
        undefined
      ).pipe(
        finalize(() => {
          this.isLoading.emit(false);
        })
      ).subscribe({
        next: (response) => {
          if (response && response.items && response.items.length > 0) {
            if (response.items.length > 1) {
              this.multipleInvoices = response.items;
              this.openInvoiceSelectionModal();
            } else {
              const invoice = response.items[0];
              this.navigateToConference(invoice);
            }
          } else {
            this.audioService.playBeep(5);
            this.toasterService.triggerAlert('Nota fiscal não encontrada', 'error');
            this.errorMessage = 'Nota fiscal não encontrada';
          }
        },
        error: (error) => {
          this.audioService.playBeep(5);
          this.toasterService.triggerAlert('Erro ao buscar nota fiscal', 'error');
          this.errorMessage = 'Erro ao buscar nota fiscal';
        }
      });
    }
  }

  private loadInvoiceList() {
    const branchCode = Number(this.tokenService.getBranch());
    if (!branchCode) return;

    if (this.currentPage === 1) {
      this.isLoading.emit(true);
    }
    
    this.errorMessage = null;

    const filterDate = this.coerceToDate(this.filterForm.get('filterDate')?.value) ?? new Date();
    const invoiceDateStr = this.toYMD(filterDate);

    this.embarqueService.getCheckingInvoices(
      branchCode,
      undefined,
      undefined,
      this.currentPage,
      this.pageSize,
      invoiceDateStr
    ).pipe(
      finalize(() => {
        this.isLoading.emit(false);
        this.isLoadingMore = false;
      })
    ).subscribe({
      next: (response) => {
        if (response && response.items) {
          const newItems = response.items.map((item: any) => {
            const issueDate = item.invoiceDate || item.issueDate || item.issueDateTime || null;
            const formattedDate = issueDate ? this.formatDateOnly(issueDate) : '';

            return {
              notaFiscal: item.invoiceCode ?? item.invoiceNumber ?? '',
              pedido: item.orderCode ?? item.salesOrderCode ?? '',
              branchCode: item.branchCode ?? '',
              fornecedor: item.personName ?? item.supplierName ?? '',
              dataEmissao: formattedDate,
              status: item.status ?? (item.statusType === 2 ? 'checked' : 'pending'),
              conferenceStatus: item.conferenceStatus ?? '',
              serialCode: Number(String(item.serialCode ?? '0').trim())
            };
          });
          
          if (this.currentPage === 1) {
            this.invoiceList = newItems;
            this.totalPages = response.totalPages || 1;
            this.totalItems = response.totalItems || response.count || 0;
          } else {
            this.invoiceList = [...this.invoiceList, ...newItems];
          }
          this.hasNextPage = response.hasNext || false;
          
          this.applyStatusFilter();
        } else {
          if (this.currentPage === 1) {
            this.invoiceList = [];
            this.displayInvoiceList = [];
          }
        }
      },
      error: (error) => {
        this.audioService.playBeep(5);
        this.toasterService.triggerAlert('Erro ao carregar conferências', 'error');
        if (this.currentPage === 1) {
          this.invoiceList = [];
          this.displayInvoiceList = [];
        }
        this.errorMessage = 'Erro ao carregar conferências';
      }
    });
  }

  private formatDateOnly(dateLike: any): string {
    const d = new Date(dateLike);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR');
  }

  private coerceToDate(val: any): Date | null {
    if (!val) return null;
    if (val instanceof Date) {
      return isNaN(val.getTime()) ? null : val;
    }
    if (typeof val === 'string') {
      const ddmmyyyy = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (ddmmyyyy) {
        const d = Number(ddmmyyyy[1]);
        const m = Number(ddmmyyyy[2]);
        const y = Number(ddmmyyyy[3]);
        const dt = new Date(y, m - 1, d);
        return isNaN(dt.getTime()) ? null : dt;
      }
      const ymd = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymd) {
        const y = Number(ymd[1]);
        const m = Number(ymd[2]);
        const d = Number(ymd[3]);
        const dt = new Date(y, m - 1, d);
        return isNaN(dt.getTime()) ? null : dt;
      }
      const dt = new Date(val);
      return isNaN(dt.getTime()) ? null : dt;
    }
    return null;
  }

  private toYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  onStatusFilterChange(value: string) {
    this.statusFilter = value;
    this.currentPage = 1;
    this.invoiceList = [];
    this.displayInvoiceList = [];
  }

  onFilterClick() {
    this.storeCurrentFilters();
    this.currentPage = 1;
    this.invoiceList = [];
    this.displayInvoiceList = [];
    this.loadInvoiceList();
  }

  private normalizeItemStatus(item: any): string {
    let st: string = (item.conferenceStatus || item.status || '').toString().toLowerCase().trim();
    if (!st) return 'pending';

    if (st === 'completed' || st === 'concluida' || st === 'concluído' || st === 'concluída' || st === 'done' || st === 'finalizado') {
      return 'checked';
    }
    if (st === 'in_progress' || st === 'in-progress' || st === 'inconference' || st === 'in conference' || st === 'andamento' || st === 'em andamento') {
      return 'in-progress';
    }
    if (st === 'pendente' || st === 'pending') {
      return 'pending';
    }
    return st;
  }

  private applyStatusFilter() {
    const filteredItems = this.statusFilter === 'all' 
      ? [...this.invoiceList]
      : this.invoiceList.filter(item => this.normalizeItemStatus(item) === this.statusFilter);
    
    if (this.currentPage === 1) {
      this.displayInvoiceList = filteredItems;
    } else {
      const previousCount = this.displayInvoiceList.length;
      const newFilteredItems = filteredItems.slice(previousCount);
      this.displayInvoiceList = [...this.displayInvoiceList, ...newFilteredItems];
    }
    this.filteredTotalItems = this.statusFilter === 'all' ? this.totalItems : this.displayInvoiceList.length;
  }

  private storeCurrentFilters() {
    try {
      const rawDate = this.filterForm.get('filterDate')?.value ?? null;
      const date = this.coerceToDate(rawDate);

      const toParts = (d: Date | null) => d ? ({ y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() }) : null;

      const payload = {
        filterDate: toParts(date),
        statusFilter: this.statusFilter
      } as any;

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }

  private loadStoredFilters(): { filterDate?: Date; statusFilter?: string } | null {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;
      const parsed: any = JSON.parse(raw);

      const fromParts = (p: any): Date | undefined => {
        if (!p) return undefined;
        if (typeof p === 'string') {
          const m = p.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (m) {
            const y = Number(m[1]);
            const mo = Number(m[2]);
            const d = Number(m[3]);
            return new Date(y, mo - 1, d);
          }
          const dt = new Date(p);
          return isNaN(dt.getTime()) ? undefined : dt;
        }
        if (typeof p.y === 'number' && typeof p.m === 'number' && typeof p.d === 'number') {
          return new Date(p.y, p.m - 1, p.d);
        }
        return undefined;
      };

      const filterDate = parsed?.filterDate ? fromParts(parsed.filterDate) : (parsed?.start ? fromParts(parsed.start) : fromParts(parsed?.startDate));
      const statusFilter = typeof parsed?.statusFilter === 'string' ? parsed.statusFilter : undefined;
      return { filterDate, statusFilter };
    } catch {
      return null;
    }
  }

  getConferenceStatusInfo(status: string) {
    if (!status) {
      return { text: 'Indefinido', icon: 'po-icon-warning', class: 'conference-status-badge conference-status-error' };
    }
    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'pending':
      case 'aguardando':
        return { text: 'Pendente', icon: 'po-icon-clock', class: 'conference-status-badge conference-status-pending' };
      case 'inconference':
      case 'in_progress':
      case 'andamento':
      case 'in-progress':
        return { text: 'Em conferência', icon: 'po-icon-refresh', class: 'conference-status-badge conference-status-in-progress' };
      case 'checked':
      case 'completed':
      case 'concluida':
      case 'concluído':
        return { text: 'Concluída', icon: 'po-icon-ok', class: 'conference-status-badge conference-status-completed' };
      case 'error':
      case 'falha':
        return { text: 'Falha', icon: 'po-icon-error', class: 'conference-status-badge conference-status-error' };
      default:
        return { text: status, icon: 'po-icon-info', class: 'conference-status-badge conference-status-in-progress' };
    }
  }

  private openInvoiceSelectionModal(): void {
    this.selectedInvoice = null;
    this.modalSelectInvoice.open();
  }

  onInvoiceSelect(invoice: any): void {
    this.selectedInvoice = invoice;
  }

  confirmInvoiceSelection(): void {
    if (!this.selectedInvoice) {
      this.toasterService.triggerAlert('Selecione uma nota fiscal', 'warning');
      return;
    }
    this.modalSelectInvoice.close();
    this.navigateToConference(this.selectedInvoice);
  }

  cancelInvoiceSelection(): void {
    this.multipleInvoices = [];
    this.selectedInvoice = null;
    this.modalSelectInvoice.close();
  }

  private navigateToConference(invoice: any): void {
    this.router.navigate(['/embarque'], { 
      queryParams: {
        branchCode: invoice.branchCode,
        invoiceSequence: invoice.invoiceSequence,
        invoiceDate: invoice.invoiceDate,
        invoiceCode: invoice.invoiceCode,
        serialCode: invoice.serialCode,
        issueDate: invoice.issueDate,
        personCode: invoice.personCode,
        personName: invoice.personName,
        accessKey: invoice.accessKey,
        orderBranchCode: invoice.orderBranchCode,
        orderCode: invoice.orderCode,
        orderNumber: invoice.orderNumber,
        conferenceStatus: invoice.conferenceStatus
      }
    });
  }

  formatInvoiceDate(date: string): string {
    if (!date) return '-';
    return this.formatDateOnly(date);
  }

  onScroll(event: any): void {
    const element = event.target;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    
    if (atBottom && this.hasNextPage && !this.isLoadingMore) {
      this.loadMoreInvoices();
    }
  }

  loadMoreInvoices(): void {
    if (this.isLoadingMore || !this.hasNextPage) return;
    
    this.isLoadingMore = true;
    this.currentPage++;
    this.loadInvoiceList();
  }

}
